import { FunctionComponent } from "react";

import ReactDOM from "react-dom";

import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";

import { useTranslation } from "react-i18next";

import { Icon } from "@/components";

import { undoToastHeadline } from "../utils/composerSendRequest.util";

interface UndoSendToastProps {
	/** How many messages the pending send holds (1 reply + N follow-ups). */
	messageCount: number;
	/** The send waits for a pending invitation: days after the acceptance (PREC-4216), else null. */
	acceptanceDelayDays?: number | null;
	onUndo: () => void;
	/** Disables the Undo button while the cancel request is in flight. */
	isUndoing?: boolean;
	/** Portal target (extension passes its shadow root); defaults to `document.body`. */
	portalContainer?: HTMLElement | null;
}

/**
 * Post-send toast with an Undo affordance ("Reply sent · 2 follow-ups
 * scheduled · Undo"). Purely presentational: the undo window timer lives in
 * {@link useUndoableSend} — the toast disappears when the pending send commits
 * or is undone, so it needs no auto-hide of its own.
 */
export const UndoSendToast: FunctionComponent<UndoSendToastProps> = ({
	messageCount,
	acceptanceDelayDays = null,
	onUndo,
	isUndoing = false,
	portalContainer,
}) => {
	const { t } = useTranslation();

	const followUpCount = messageCount - 1;
	// A message parked behind a pending invitation was not sent: say when it goes (PREC-4216).
	const headline = undoToastHeadline(acceptanceDelayDays, t);
	const text =
		followUpCount > 0
			? `${headline} · ${t("inbox.undoSend.followUpsScheduled", { count: followUpCount })}`
			: headline;

	return ReactDOM.createPortal(
		<div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 rounded-xl bg-gray-900 px-4 py-2.5 text-sm text-white shadow-lg">
			<Icon className="w-4 h-4 shrink-0" icon={faCircleCheck} />
			<span>{text}</span>
			<button
				className="rounded-lg bg-white/15 px-3 py-1 text-sm font-bold text-white cursor-pointer hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-60"
				disabled={isUndoing}
				type="button"
				onClick={onUndo}
			>
				{t("inbox.undoSend.undo")}
			</button>
		</div>,
		portalContainer ?? document.body
	);
};
